import { useEffect, useMemo, useState } from "react";
import { estimateGenerationCost } from "../services/generationCostService";

const estimateCache = new Map();
const CACHE_LIMIT = 200;

const stableRequestKey = (request) => JSON.stringify(
  Object.keys(request || {})
    .sort()
    .reduce((result, key) => {
      const value = request[key];
      if (value !== undefined && value !== null && value !== "") result[key] = value;
      return result;
    }, {}),
);

const rememberEstimate = (key, value) => {
  if (estimateCache.size >= CACHE_LIMIT) {
    estimateCache.delete(estimateCache.keys().next().value);
  }
  estimateCache.set(key, value);
};

export const useGenerationCostEstimate = (request, delay = 180) => {
  const requestKey = useMemo(
    () => (request ? stableRequestKey(request) : ""),
    [request],
  );
  const [state, setState] = useState(() => ({
    estimate: requestKey ? estimateCache.get(requestKey) || null : null,
    loading: Boolean(requestKey && !estimateCache.has(requestKey)),
  }));

  useEffect(() => {
    if (!requestKey) {
      setState({ estimate: null, loading: false });
      return undefined;
    }

    const cached = estimateCache.get(requestKey);
    if (cached) {
      setState({ estimate: cached, loading: false });
      return undefined;
    }

    let cancelled = false;
    setState((current) => ({ estimate: current.estimate, loading: true }));
    const timer = window.setTimeout(async () => {
      try {
        const estimate = await estimateGenerationCost(JSON.parse(requestKey));
        if (cancelled) return;
        rememberEstimate(requestKey, estimate);
        setState({ estimate, loading: false });
      } catch {
        if (cancelled) return;
        const unavailable = {
          available: false,
          estimatedPoints: 0,
          note: "生成完成后按实际用量结算",
        };
        rememberEstimate(requestKey, unavailable);
        setState({ estimate: unavailable, loading: false });
      }
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [delay, requestKey]);

  return state;
};

export default useGenerationCostEstimate;
